import { useState } from 'react';
import { Icon, MetaGrid, MetaItem, Panel, RuleId, Screen, ScreenHeader, Tabs } from '@garnet/ui';
import type { AutomotiveInput, ClassifyResult, MedicalInput } from '../data/types.js';
import type { StudioContext } from '../studio.js';
import { StateBlock } from '../components.js';

type Domain = 'medical' | 'automotive';

const MED_PRESETS: { name: string; inp: MedicalInput }[] = [
  { name: 'Surgical drape', inp: { invasive: 'none', active: false, duration: 'transient', implantable: false, contact: 'none', purpose: [], hazardous: false } },
  { name: 'Infusion pump', inp: { invasive: 'none', active: true, duration: 'short_term', implantable: false, contact: 'none', purpose: ['administer_medicine'], hazardous: true } },
  { name: 'Hip prosthesis', inp: { invasive: 'surgical', active: false, duration: 'long_term', implantable: true, contact: 'none', purpose: [], hazardous: false } },
  { name: 'Coronary stent', inp: { invasive: 'surgical', active: false, duration: 'long_term', implantable: true, contact: 'heart_cns', purpose: [], hazardous: false } },
  { name: 'Absorbable suture', inp: { invasive: 'surgical', active: false, duration: 'transient', implantable: false, contact: 'none', purpose: [], hazardous: false } },
  { name: 'Nanomaterial dressing', inp: { invasive: 'none', active: false, duration: 'transient', implantable: false, contact: 'none', purpose: [], hazardous: false, nanomaterial: true } },
];

function Seg<V extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: V;
  options: { v: V; label?: string }[];
  onChange: (v: V) => void;
}) {
  return (
    <div>
      <span className="field-label">{label}</span>
      <div
        style={{ display: 'flex', gap: 0, border: '1px solid var(--border)', borderRadius: 4, overflow: 'hidden', width: 'fit-content', flexWrap: 'wrap' }}
      >
        {options.map((o, i) => (
          <button
            key={o.v}
            onClick={() => onChange(o.v)}
            className="mono"
            style={{
              padding: '6px 11px',
              fontSize: 11.5,
              background: value === o.v ? 'var(--garnet-muted)' : 'transparent',
              color: value === o.v ? 'var(--garnet-text)' : 'var(--fg-2)',
              borderLeft: i ? '1px solid var(--border-subtle)' : 'none',
            }}
          >
            {o.label || o.v}
          </button>
        ))}
      </div>
    </div>
  );
}

function Toggle({ label, value, onChange }: { label: string; value: boolean; onChange: (v: boolean) => void }) {
  return (
    <button onClick={() => onChange(!value)} style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
      <span
        style={{
          width: 32,
          height: 18,
          borderRadius: 9,
          background: value ? 'var(--garnet)' : 'var(--bg-elevated)',
          border: '1px solid ' + (value ? 'var(--garnet)' : 'var(--border)'),
          position: 'relative',
          transition: 'all 150ms',
          flexShrink: 0,
        }}
      >
        <span
          style={{
            position: 'absolute',
            top: 2,
            left: value ? 16 : 2,
            width: 12,
            height: 12,
            borderRadius: '50%',
            background: value ? '#fff' : 'var(--fg-3)',
            transition: 'left 150ms',
          }}
        />
      </span>
      <span className="field-label" style={{ margin: 0 }}>
        {label}
      </span>
    </button>
  );
}

function ResultPanel({ result, onOpenRule }: { result: ClassifyResult | null; onOpenRule: (id: string) => void }) {
  if (!result) {
    return (
      <StateBlock
        kind="empty"
        title="awaiting a device"
        body="Set the device’s characteristics and run the check. The result is decided by rule only — no AI."
      />
    );
  }
  if (!result.ok) {
    return (
      <div className="fade-in">
        <StateBlock kind="abstain" title="Abstains — no rule applies" body={result.reason} />
        <div className="metatext" style={{ marginTop: 12, display: 'flex', gap: 6, alignItems: 'flex-start' }}>
          <Icon name="shield" size={12} color="var(--abstain-text)" style={{ marginTop: 1 }} />
          The Advisor declines and the engine returns nothing — neither offers a guess.
        </div>
      </div>
    );
  }
  return (
    <div className="fade-in">
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 4 }}>
        <span className="serif" style={{ fontSize: result.class.length > 14 ? 26 : 40, color: 'var(--garnet)', lineHeight: 1.05 }}>
          {result.class}
        </span>
        {result.soft && (
          <span className="badge b-warn">
            <span className="dot" style={{ background: 'var(--status-warn)' }} />
            braking abstains
          </span>
        )}
      </div>
      <div className="metatext" style={{ marginBottom: 14 }}>
        this is the class, and the rule behind it
      </div>
      <div
        onClick={() => onOpenRule(result.rule)}
        role="button"
        style={{ cursor: 'pointer', background: 'var(--bg-sunken)', border: '1px solid var(--border)', borderRadius: 6, padding: '11px 13px', marginBottom: 12 }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <RuleId id={result.rule} />
          <Icon name="arrow" size={13} color="var(--fg-3)" />
        </div>
        <div className="cite" style={{ marginTop: 6 }}>
          {result.cite}
        </div>
      </div>
      <MetaGrid labelWidth={120}>
        <MetaItem label="From release">
          <span className="mono">release 2026.06.1</span>
        </MetaItem>
        <MetaItem label="Device naming">
          <span className="mono">EMDN → GMDN, 2026.1</span>
        </MetaItem>
        <MetaItem label="Decided by">
          <span style={{ color: 'var(--status-pass-text)' }}>rule only — no AI</span>
        </MetaItem>
      </MetaGrid>
      {result.abstainNote && (
        <div
          className="metatext"
          style={{ marginTop: 12, display: 'flex', gap: 6, alignItems: 'flex-start', padding: '8px 10px', background: 'var(--abstain-bg)', borderRadius: 4 }}
        >
          <Icon name="shield" size={12} color="var(--abstain-text)" style={{ marginTop: 1 }} />
          {result.abstainNote}
        </div>
      )}
    </div>
  );
}

function GoldenSet({ ctx, domain }: { ctx: StudioContext; domain: Domain }) {
  const set = ctx.api.goldenSet(domain);
  const [results, setResults] = useState<(string | null)[] | null>(null);
  const [running, setRunning] = useState(false);
  const run = () => {
    setRunning(true);
    setResults(set.map(() => 'running'));
    set.forEach((g, i) => {
      setTimeout(() => {
        const r = ctx.api.classify(domain, g.inp);
        const got = r.ok ? r.class : 'Abstain';
        setResults((prev) => {
          const n = prev ? [...prev] : set.map(() => null);
          n[i] = got === g.expect ? 'pass' : 'fail';
          return n;
        });
      }, 200 + i * 260);
    });
    setTimeout(() => setRunning(false), 200 + set.length * 260 + 100);
  };
  const passing = results ? results.filter((r) => r === 'pass').length : 0;
  return (
    <Panel
      flush
      title="Reference cases"
      right={
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {results && (
            <span className="mono metatext" style={{ color: passing === set.length ? 'var(--status-pass-text)' : 'var(--status-warn-text)' }}>
              {passing}/{set.length} checked
            </span>
          )}
          <button className="btn btn-sm mono" onClick={run} disabled={running}>
            <Icon name="beaker" size={12} />
            {running ? 'checking…' : 'Run cases'}
          </button>
        </div>
      }
    >
      <table className="tbl">
        <thead>
          <tr>
            <th>Example</th>
            <th>Expected</th>
            <th style={{ textAlign: 'right' }}>Result</th>
          </tr>
        </thead>
        <tbody>
          {set.map((g, i) => {
            const st = results ? results[i] : null;
            return (
              <tr key={i} style={{ cursor: 'default' }}>
                <td style={{ color: 'var(--fg-1)' }}>{g.name}</td>
                <td className="mono metatext">{g.expect}</td>
                <td style={{ textAlign: 'right' }}>
                  {!st ? (
                    <span className="metatext mono">—</span>
                  ) : st === 'running' ? (
                    <span className="badge b-info pulse">
                      <span className="dot" style={{ background: 'var(--status-info-text)' }} />…
                    </span>
                  ) : st === 'pass' ? (
                    <span className="badge b-pass">
                      <Icon name="check" size={11} />
                      pass
                    </span>
                  ) : (
                    <span className="badge b-fail">
                      <Icon name="x" size={11} />
                      fail
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div
        className="metatext"
        style={{ padding: '10px var(--pad-x)', borderTop: '1px solid var(--border-subtle)', display: 'flex', gap: 6, alignItems: 'center' }}
      >
        <Icon name="history" size={12} color="var(--fg-3)" />
        If any case fails, the change is blocked — the example becomes a permanent check.
      </div>
    </Panel>
  );
}

export function Classifier({ ctx }: { ctx: StudioContext }) {
  const { api } = ctx;
  const [domain, setDomain] = useState<Domain>('medical');
  const [result, setResult] = useState<ClassifyResult | null>(null);
  const switchDomain = (d: Domain) => {
    setDomain(d);
    setResult(null);
  };

  // medical form state
  const [med, setMed] = useState<MedicalInput>({
    invasive: 'none',
    active: false,
    duration: 'transient',
    implantable: false,
    contact: 'none',
    purpose: [],
    hazardous: true,
    nanomaterial: false,
  });
  const setM = <K extends keyof MedicalInput>(k: K, v: MedicalInput[K]) => setMed((p) => ({ ...p, [k]: v }));
  const togglePurpose = (v: string) =>
    setMed((p) => ({ ...p, purpose: p.purpose.includes(v) ? p.purpose.filter((x) => x !== v) : [...p.purpose, v] }));

  // automotive form state
  const [auto, setAuto] = useState<AutomotiveInput>({ use: 'passenger', seats: 5, massKg: 1400 });
  const setA = <K extends keyof AutomotiveInput>(k: K, v: AutomotiveInput[K]) => setAuto((p) => ({ ...p, [k]: v }));

  return (
    <Screen>
      <ScreenHeader
        eyebrow="§ Classification check"
        title="Confirm a class"
        lead="Enter a device’s characteristics and see the class it gets and the rule behind it. Decided by rule only — no AI."
        actions={
          <Tabs
            tabs={[
              { key: 'medical', label: 'Medical devices' },
              { key: 'automotive', label: 'Vehicles' },
            ]}
            active={domain}
            onChange={switchDomain}
          />
        }
      />

      <div className="cs-classify-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 380px', gap: 'var(--gap)', alignItems: 'start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gap)', minWidth: 0 }}>
          <Panel
            title={domain === 'medical' ? 'Device characteristics' : 'Vehicle attributes'}
            right={<span className="mono metatext">the facts</span>}
          >
            {domain === 'medical' ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div>
                  <span className="field-label">Examples</span>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {MED_PRESETS.map((pr) => (
                      <button key={pr.name} className="btn btn-sm" style={{ fontSize: 11 }} onClick={() => setMed({ ...pr.inp })}>
                        {pr.name}
                      </button>
                    ))}
                  </div>
                </div>
                <hr className="hr" />
                <Seg
                  label="Invasiveness"
                  value={med.invasive}
                  onChange={(v) => setM('invasive', v)}
                  options={[{ v: 'none' }, { v: 'body_orifice', label: 'orifice' }, { v: 'surgical' }]}
                />
                <Seg
                  label="Duration"
                  value={med.duration}
                  onChange={(v) => setM('duration', v)}
                  options={[{ v: 'transient' }, { v: 'short_term', label: 'short' }, { v: 'long_term', label: 'long' }]}
                />
                <Seg
                  label="Body contact"
                  value={med.contact}
                  onChange={(v) => setM('contact', v)}
                  options={[{ v: 'none' }, { v: 'teeth' }, { v: 'heart_cns', label: 'heart/CNS' }]}
                />
                <div style={{ display: 'flex', gap: 22, flexWrap: 'wrap' }}>
                  <Toggle label="active device" value={med.active} onChange={(v) => setM('active', v)} />
                  <Toggle label="implantable" value={med.implantable} onChange={(v) => setM('implantable', v)} />
                  <Toggle label="hazardous manner" value={med.hazardous} onChange={(v) => setM('hazardous', v)} />
                </div>
                <div>
                  <span className="field-label">Intended purpose</span>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {(
                      [
                        ['administer_medicine', 'administers a medicine'],
                        ['diagnosis', 'for diagnosis'],
                        ['monitoring', 'for monitoring'],
                      ] as [string, string][]
                    ).map(([v, lbl]) => (
                      <button
                        key={v}
                        onClick={() => togglePurpose(v)}
                        style={{
                          padding: '5px 10px',
                          fontSize: 11.5,
                          borderRadius: 4,
                          border: '1px solid ' + (med.purpose.includes(v) ? 'var(--garnet)' : 'var(--border)'),
                          background: med.purpose.includes(v) ? 'var(--garnet-muted)' : 'transparent',
                          color: med.purpose.includes(v) ? 'var(--garnet)' : 'var(--fg-2)',
                        }}
                      >
                        {lbl}
                      </button>
                    ))}
                  </div>
                </div>
                <button className="btn btn-primary mono" onClick={() => setResult(api.classify('medical', med))}>
                  <Icon name="scale" size={13} />
                  Check the class
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div>
                  <span className="field-label">Examples</span>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {api.goldenSet('automotive').map((pr) => (
                      <button
                        key={pr.name}
                        className="btn btn-sm"
                        style={{ fontSize: 11 }}
                        onClick={() => setAuto({ ...(pr.inp as AutomotiveInput) })}
                      >
                        {pr.name}
                      </button>
                    ))}
                  </div>
                </div>
                <hr className="hr" />
                <Seg label="Use" value={auto.use} onChange={(v) => setA('use', v)} options={[{ v: 'passenger' }, { v: 'goods' }]} />
                <div style={{ display: 'flex', gap: 14 }}>
                  <div style={{ flex: 1 }}>
                    <span className="field-label">Seating capacity</span>
                    <input className="inp mono" type="number" value={auto.seats} onChange={(e) => setA('seats', +e.target.value)} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <span className="field-label">Max mass (kg)</span>
                    <input className="inp mono" type="number" value={auto.massKg} onChange={(e) => setA('massKg', +e.target.value)} />
                  </div>
                </div>
                <button className="btn btn-primary mono" onClick={() => setResult(api.classify('automotive', auto))}>
                  <Icon name="scale" size={13} />
                  Resolve category
                </button>
              </div>
            )}
          </Panel>
          <GoldenSet ctx={ctx} domain={domain} />
        </div>
        <div style={{ position: 'sticky', top: 0 }}>
          <Panel
            title="The result"
            right={
              <span className="badge b-pass" style={{ padding: '1px 6px' }}>
                <span className="dot" style={{ background: 'var(--status-pass)' }} />
                by rule
              </span>
            }
          >
            <ResultPanel result={result} onOpenRule={ctx.openRule} />
          </Panel>
        </div>
      </div>
    </Screen>
  );
}
